import React, { useState, useEffect } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'
import { toast } from 'react-toastify'

interface Comment {
    _id: string;
    blogId: string;
    blogTitle: string;
    name: string;
    email?: string;
    text: string;
    date: string;
    isApproved?: boolean;
    createdAt?: string;
}

export default function AdminBlogComments() {
    const { token } = useAuth()
    const [comments, setComments] = useState<Comment[]>([])
    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('all')

    useEffect(() => {
        fetchComments()
    }, [])

    const fetchComments = async () => {
        if (!token) return
        try {
            const res = await fetch(`${API_BASE}/blogs/comments/all`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const data = await res.json()
            if (Array.isArray(data)) {
                setComments(data)
            }
        } catch (err) {
            console.error(err)
            toast.error('Failed to fetch comments')
        } finally {
            setLoading(false)
        }
    }

    const handleApprove = async (blogId: string, commentId: string) => {
        if (!token) return
        try {
            const res = await fetch(`${API_BASE}/blogs/${blogId}/comments/${commentId}/approve`, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                toast.success('Comment approved')
                setComments(prev => prev.map(c => 
                    c._id === commentId ? { ...c, isApproved: true } : c
                ))
            } else {
                toast.error('Failed to approve comment')
            }
        } catch (err) {
            console.error(err)
            toast.error('Server error')
        }
    }

    const handleDelete = async (blogId: string, commentId: string) => {
        if (!token || !window.confirm('Are you sure you want to delete this comment?')) return
        try {
            const res = await fetch(`${API_BASE}/blogs/${blogId}/comments/${commentId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
                toast.success('Comment deleted')
                setComments(prev => prev.filter(c => c._id !== commentId))
            } else {
                toast.error('Failed to delete comment')
            }
        } catch (err) {
            console.error(err)
            toast.error('Server error')
        }
    }

    const filteredComments = comments.filter(c => {
        if (filter === 'pending') return !c.isApproved
        if (filter === 'approved') return c.isApproved
        return true
    })

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">Blog Comments</h1>
                    <p className="text-slate-600 dark:text-slate-400 text-sm">Moderate and manage comments across all your blog posts.</p>
                </div>
                <div className="flex items-center gap-2 bg-blue-50 dark:bg-slate-800 p-1 rounded-2xl">
                    <button 
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filter === 'all' ? 'bg-blue-950 text-white shadow-md' : 'text-slate-600 hover:text-blue-950'}`}
                    >
                        Total
                    </button>
                    <button 
                        onClick={() => setFilter('pending')}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filter === 'pending' ? 'bg-amber-600 text-white shadow-md' : 'text-slate-600 hover:text-blue-950'}`}
                    >
                        Pending
                    </button>
                    <button 
                        onClick={() => setFilter('approved')}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${filter === 'approved' ? 'bg-emerald-600 text-white shadow-md' : 'text-slate-600 hover:text-blue-950'}`}
                    >
                        Approved
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-slate-900 rounded-[32px] border border-blue-900/10 dark:border-slate-800 shadow-xl shadow-slate-200/50 dark:shadow-none overflow-hidden transition-all animate-in fade-in zoom-in-95 duration-500">
                <div className="p-8 border-b border-blue-900/10 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                    <div className="flex items-center gap-4">
                        <h3 className="text-xl font-bold text-blue-950 dark:text-white">Comments List</h3>
                        <span className="px-3 py-1 rounded-full bg-blue-50 dark:bg-slate-800 text-sm font-semibold text-blue-900 dark:text-blue-950">{filteredComments.length} Comments</span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-20 text-center">
                            <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                            <p className="text-blue-950 dark:text-blue-950 font-medium tracking-wide">Retrieving Community Data...</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-white/50 dark:bg-slate-800/30 border-b border-blue-900/10 dark:border-slate-800">
                                    <th className="px-8 py-4 text-sm font-semibold text-slate-500 dark:text-slate-400">Author</th>
                                    <th className="px-8 py-4 text-sm font-semibold text-slate-500 dark:text-slate-400">Comment</th>
                                    <th className="px-8 py-4 text-sm font-semibold text-slate-500 dark:text-slate-400 text-center">Status</th>
                                    <th className="px-8 py-4 text-sm font-semibold text-slate-500 dark:text-slate-400 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {filteredComments.map(c => (
                                    <tr key={c._id} className="group hover:bg-white/50 dark:hover:bg-slate-800/20 transition-all">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-2xl bg-blue-950 flex items-center justify-center text-white text-sm font-bold shadow-lg">
                                                    {c.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-blue-950 dark:text-white text-base">{c.name}</h4>
                                                    <p className="text-xs font-medium text-slate-500 mt-0.5 line-clamp-1 max-w-[150px]">{c.blogTitle}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed font-medium max-w-md line-clamp-2">{c.text}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <i className="fa-regular fa-clock text-[10px] text-slate-400"></i>
                                                <span className="text-[10px] font-medium text-slate-400">{new Date(c.createdAt || c.date).toLocaleDateString()}</span>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex justify-center">
                                                {c.isApproved ? (
                                                    <span className="px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold uppercase border border-emerald-100 dark:border-emerald-800">Live</span>
                                                ) : (
                                                    <span className="px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-[10px] font-bold uppercase border border-amber-100 dark:border-amber-800">Pending</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-8 py-6">
                                            <div className="flex items-center justify-end gap-2">
                                                {!c.isApproved && (
                                                    <button 
                                                        onClick={() => handleApprove(c.blogId, c._id)}
                                                        className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                        title="Approve Comment"
                                                    >
                                                        <i className="fa-solid fa-check text-[10px]"></i>
                                                    </button>
                                                )}
                                                <button 
                                                    onClick={() => handleDelete(c.blogId, c._id)}
                                                    className="w-9 h-9 rounded-xl bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 hover:bg-rose-600 hover:text-white transition-all flex items-center justify-center shadow-sm"
                                                    title="Remove Comment"
                                                >
                                                    <i className="fa-solid fa-trash-can text-[10px]"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredComments.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="px-8 py-20 text-center">
                                            <div className="w-20 h-20 rounded-full bg-white dark:bg-slate-800/50 flex items-center justify-center mx-auto mb-6">
                                                <i className="fa-regular fa-comment-dots text-3xl text-blue-950/10"></i>
                                            </div>
                                            <h5 className="font-bold text-blue-950 dark:text-white mb-2">No comments found</h5>
                                            <p className="text-slate-500 text-sm">There are no comments to display at the moment.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </AdminLayout>
    )
}


